/* eslint-disable  */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import DataTable from "react-data-table-component";
import {
  ArrowUpRight,
  ArrowDownRight,
  Download,
  Search,
  X,
  Loader2,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { dataActionCreators } from "../services/Actions";
import { toast } from "react-toastify";
import RenderUnionPage from "./RenderUnionPage";
import { useTheme } from "../context/ThemeContext";
import { debounce } from "lodash";

const Dashboard = () => {
  const dispatch = useDispatch();
  const {
    GetFundIncrement,
    GetFundDecrement,
    GetAllFundsForUnion,
    GetAllLoans,
    DownloadFundIncrement,
  } = bindActionCreators(dataActionCreators, dispatch);

  const { theme } = useTheme();
  const isDark =
    theme === "dark" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;

  const [activeTab, setActiveTab] = useState(2);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  // Pagination states for loans and funds
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  const data = useSelector((state) => state.data);
  const {
    fund_increment,
    fund_decrement,
    funds,
    loans,
    isLoading,
    total_fund,
    total_loans,
    paginate,
    error,
  } = data;

  useEffect(() => {
    const fetchData = async () => {
      try {
        await GetFundIncrement();
        await GetFundDecrement();
        await GetAllFundsForUnion(true, 1, perPage);
        await GetAllLoans(true, 1, perPage);
      } catch (err) {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          toast.error("Unauthorized access. Please login again.", {
            position: "top-right",
            autoClose: 5000,
          });
        }
      }
    };
    fetchData();
  }, []);

  // Check for unauthorized errors
  useEffect(() => {
    if (error) {
      if (error.status === 401 || error.status === 403) {
        toast.error("Unauthorized access. Please login again.", {
          position: "top-right",
          autoClose: 5000,
        });
      } else if (error.message) {
        toast.error(error.message, {
          position: "top-right",
          autoClose: 4000,
        });
      }
    }
  }, [error]);

  // Helper to render Profile Avatar
  const renderMemberProfile = (name, staffId) => (
    <div className="flex items-center gap-3 py-2">
      <div className="h-10 w-10 rounded-full bg-indigo-100 dark:bg-indigo-900/40 border border-indigo-200 dark:border-indigo-700 flex items-center justify-center text-indigo-700 dark:text-indigo-300 font-bold shadow-sm">
        {name ? name.charAt(0).toUpperCase() : "?"}
      </div>
      <div className="flex flex-col">
        <span className="font-semibold text-slate-800 dark:text-slate-100 leading-tight">
          {name || "Unknown"}
        </span>
        <span className="text-xs text-slate-500 dark:text-slate-400 font-mono tracking-wider">
          {staffId}
        </span>
      </div>
    </div>
  );

  const incrementDecrementColumns = [
    {
      name: "MEMBER PROFILE",
      selector: (row) => row.name,
      cell: (row) => renderMemberProfile(row.name, row.staff_id),
      sortable: true,
      grow: 2,
    },
    {
      name: "CURRENT (GHS)",
      selector: (row) => row.current_contribution,
      cell: (row) => (
        <span className="font-semibold text-slate-700 dark:text-slate-200">
          ₵ {Number(row.current_contribution).toLocaleString()}
        </span>
      ),
      sortable: true,
    },
    {
      name: "REQUESTED (GHS)",
      selector: (row) => row.requested_increment,
      cell: (row) => (
        <span
          className={`font-bold ${
            activeTab === 2 ? "text-green-600" : "text-red-600"
          }`}
        >
          {activeTab === 2 ? "+" : "-"} ₵{" "}
          {Number(row.requested_increment).toLocaleString()}
        </span>
      ),
      sortable: true,
    },
    {
      name: "CONTACT",
      selector: (row) => row.phone_number,
      cell: (row) => (
        <span className="text-slate-500 dark:text-slate-400 text-sm">
          {row.phone_number || "N/A"}
        </span>
      ),
    },
  ];

  const deductionColumns = [
    {
      name: "MEMBER PROFILE",
      selector: (row) => row.staff_id,
      cell: (row) =>
        renderMemberProfile(row.user?.name || "Member", row.staff_id),
      grow: 2,
    },
    {
      name: "AMOUNT",
      selector: (row) => row.deduction,
      cell: (row) => (
        <span className="font-bold text-orange-600">
          ₵ {(row.deduction || row.balance || 0).toLocaleString()}
        </span>
      ),
    },
    {
      name: "PERIOD",
      selector: (row) => row.period,
      cell: (row) => (
        <span className="bg-slate-100 dark:bg-gray-700 text-slate-600 dark:text-slate-300 px-3 py-1 rounded-md text-xs font-medium uppercase">
          {row.period || "N/A"}
        </span>
      ),
    },
    {
      name: "TRANSACTION",
      selector: (row) => row.transaction_type,
      cell: (row) => (
        <span className="px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 text-[10px] font-bold border border-blue-100 dark:border-blue-800">
          {row.transaction_type || "DEDUCTION"}
        </span>
      ),
    },
  ];

  const loanDeductionColumns = [
    {
      name: "MEMBER PROFILE",
      selector: (row) => row.staff_id,
      cell: (row) =>
        renderMemberProfile(row.user?.name || "Member", row.staff_id),
      grow: 2,
    },
    {
      name: "DEDUCTION AMOUNT",
      selector: (row) => row.deduction,
      cell: (row) => (
        <span className="font-bold text-red-600">
          ₵ {Number(row.deduction || 0).toLocaleString()}
        </span>
      ),
      sortable: true,
    },
    {
      name: "PERIOD",
      selector: (row) => row.period,
      cell: (row) => (
        <span className="bg-slate-100 dark:bg-gray-700 text-slate-600 dark:text-slate-300 px-3 py-1 rounded-md text-xs font-medium uppercase">
          {row.period || "N/A"}
        </span>
      ),
    },
    {
      name: "TRANSACTION TYPE",
      selector: (row) => row.transaction_type,
      cell: (row) => (
        <span className="px-3 py-1 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 text-xs font-bold">
          {row.transaction_type || "LOAN DED"}
        </span>
      ),
    },
  ];

  // Get current data based on active tab
  const getCurrentData = useCallback(() => {
    if (activeTab === 2) return fund_increment || [];
    if (activeTab === 3) return fund_decrement || [];
    if (activeTab === 4) return funds || [];
    if (activeTab === 5) return loans || [];
    return [];
  }, [activeTab, fund_increment, fund_decrement, funds, loans]);

  // Debounced search for loans
  const debouncedLoanSearch = useCallback(
    debounce((searchValue) => {
      if (activeTab === 5) {
        const searchParam = searchValue ? `&search=${searchValue}` : "";
        GetAllLoans(true, 1, perPage, searchParam).catch((err) => {
          if (err?.response?.status === 401 || err?.response?.status === 403) {
            toast.error("Unauthorized access. Please login again.");
          }
        });
        setCurrentPage(1);
        setIsSearching(!!searchValue);
      }
    }, 800),
    [activeTab, perPage]
  );

  // Debounced search for funds
  const debouncedFundSearch = useCallback(
    debounce((searchValue) => {
      if (activeTab === 4) {
        const searchParam = searchValue ? `&search=${searchValue}` : "";
        GetAllFundsForUnion(true, 1, perPage, searchParam).catch((err) => {
          if (err?.response?.status === 401 || err?.response?.status === 403) {
            toast.error("Unauthorized access. Please login again.");
          }
        });
        setCurrentPage(1);
        setIsSearching(!!searchValue);
      }
    }, 800),
    [activeTab, perPage]
  );

  // Filter data based on search term (client-side for non-loan/fund tabs)
  const filteredData = useMemo(() => {
    const currentData = getCurrentData();

    // For loans and funds tab, don't filter client-side
    if (activeTab === 5 || activeTab === 4) {
      return currentData;
    }

    if (!searchTerm.trim()) return currentData;

    const term = searchTerm.toLowerCase();

    return currentData.filter((item) => {
      const name = (item.name || item.user?.name || "").toLowerCase();
      const staffId = item.staff_id?.toLowerCase() || "";
      const phone = (
        item.phone_number ||
        item.user?.phone_number ||
        ""
      ).toLowerCase();

      return (
        name.includes(term) || staffId.includes(term) || phone.includes(term)
      );
    });
  }, [searchTerm, getCurrentData, activeTab]);

  // Calculate search total for loans and funds from ALL records found (across all pages)
  const searchTotal = useMemo(() => {
    if ((activeTab === 4 || activeTab === 5) && searchTerm) {
      // If backend provides searchTotal for all records, use it
      if (paginate?.searchTotal !== undefined) {
        return Number(paginate.searchTotal);
      }
    }
    return 0;
  }, [activeTab, searchTerm, paginate]);

  // Get total records count for display
  const totalRecordsFound = useMemo(() => {
    if ((activeTab === 4 || activeTab === 5) && searchTerm) {
      return paginate?.totalItems || 0;
    }
    return 0;
  }, [activeTab, searchTerm, paginate]);

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (activeTab === 5) {
      debouncedLoanSearch(value);
    } else if (activeTab === 4) {
      debouncedFundSearch(value);
    }
  };

  const getExportTypeByTab = () => {
    const types = { 2: "increment", 3: "decrement" };
    return types[activeTab] || "increment";
  };

  const handleExport = () => {
    if (activeTab === 4 || activeTab === 5) return;

    if (!startDate || !endDate) {
      toast.error("Please select date range", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    DownloadFundIncrement({
      startDate,
      endDate,
      exportType: getExportTypeByTab(),
    }).catch((err) => {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        toast.error("Unauthorized access. Please login again.");
      } else {
        toast.error("Failed to download report. Please try again.");
      }
    });

    setIsExportModalOpen(false);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setIsSearching(false);
    setCurrentPage(1);

    if (activeTab === 5) {
      GetAllLoans(true, 1, perPage).catch((err) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          toast.error("Unauthorized access. Please login again.");
        }
      });
    } else if (activeTab === 4) {
      GetAllFundsForUnion(true, 1, perPage).catch((err) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          toast.error("Unauthorized access. Please login again.");
        }
      });
    }
  };

  // Handle pagination for loans and funds
  const handlePageChange = (page) => {
    setCurrentPage(page);
    const searchParam = searchTerm ? `&search=${searchTerm}` : "";

    if (activeTab === 5) {
      GetAllLoans(true, page, perPage, searchParam).catch((err) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          toast.error("Unauthorized access. Please login again.");
        }
      });
    } else if (activeTab === 4) {
      GetAllFundsForUnion(true, page, perPage, searchParam).catch((err) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          toast.error("Unauthorized access. Please login again.");
        }
      });
    }
  };

  // Handle rows per page change
  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
    const searchParam = searchTerm ? `&search=${searchTerm}` : "";

    if (activeTab === 5) {
      GetAllLoans(true, page, newPerPage, searchParam).catch((err) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          toast.error("Unauthorized access. Please login again.");
        }
      });
    } else if (activeTab === 4) {
      GetAllFundsForUnion(true, page, newPerPage, searchParam).catch((err) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          toast.error("Unauthorized access. Please login again.");
        }
      });
    }
  };

  const customStyles = {
    table: {
      style: {
        backgroundColor: isDark ? "#1F2937" : "#FFFFFF",
      },
    },
    headRow: {
      style: {
        backgroundColor: isDark ? "#111827" : "#F9FAFB",
        borderTop: `1px solid ${isDark ? "#374151" : "#E5E7EB"}`,
        borderBottomColor: isDark ? "#374151" : "#E5E7EB",
      },
    },
    headCells: {
      style: {
        color: isDark ? "#9CA3AF" : "#6B7280",
      },
    },
    rows: {
      style: {
        minHeight: "72px",
        backgroundColor: isDark ? "#1F2937" : "#FFFFFF",
        color: isDark ? "#E5E7EB" : "#374151",
        borderBottomColor: isDark ? "#374151" : "#E5E7EB",
      },
      highlightOnHoverStyle: {
        backgroundColor: isDark ? "#374151" : "#F9FAFB",
        borderBottomColor: isDark ? "#4B5563" : "#E5E7EB",
        outline: "none",
      },
    },
    pagination: {
      style: {
        backgroundColor: isDark ? "#1F2937" : "#FFFFFF",
        color: isDark ? "#9CA3AF" : "#6B7280",
        borderTopColor: isDark ? "#374151" : "#E5E7EB",
      },
      pageButtonsStyle: {
        color: isDark ? "#9CA3AF" : "#6B7280",
        fill: isDark ? "#9CA3AF" : "#6B7280",
        "&:disabled": {
          color: isDark ? "#4B5563" : "#D1D5DB",
          fill: isDark ? "#4B5563" : "#D1D5DB",
        },
        "&:hover:not(:disabled)": {
          backgroundColor: isDark ? "#374151" : "#F3F4F6",
        },
      },
    },
  };

  return (
    <RenderUnionPage
      title="Finance Dashboard"
      page={activeTab}
      onTabChange={(tab) => {
        setActiveTab(tab);
        clearSearch();
        setCurrentPage(1);

        if (tab === 5) {
          GetAllLoans(true, 1, perPage).catch((err) => {
            if (
              err?.response?.status === 401 ||
              err?.response?.status === 403
            ) {
              toast.error("Unauthorized access. Please login again.");
            }
          });
        } else if (tab === 4) {
          GetAllFundsForUnion(true, 1, perPage).catch((err) => {
            if (
              err?.response?.status === 401 ||
              err?.response?.status === 403
            ) {
              toast.error("Unauthorized access. Please login again.");
            }
          });
        }
      }}
    >
      {/* Full Page Loader */}
      {isLoading && (
        <div className="fixed inset-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
            <p className="text-slate-600 dark:text-slate-300 font-medium">
              Loading data...
            </p>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto space-y-6 p-2 md:p-6">
        {/* STATS SECTION */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Increments"
            value={fund_increment?.length || 0}
            icon={<ArrowUpRight className="text-emerald-500" />}
            color="emerald"
          />
          <StatCard
            title="Total Decrements"
            value={fund_decrement?.length || 0}
            icon={<ArrowDownRight className="text-rose-500" />}
            color="rose"
          />
          <StatCard
            title="Total Deductions"
            value={total_fund ? `₵${total_fund.toLocaleString()}` : "₵0"}
            subtitle="Fund Amount"
            icon={<Download className="text-amber-500" />}
            color="amber"
          />
          <StatCard
            title="Total Loans"
            value={total_loans ? `₵${total_loans.toLocaleString()}` : "₵0"}
            subtitle={
              paginate?.totalItems
                ? `${paginate.totalItems.toLocaleString()} Records`
                : "Loan Deductions"
            }
            icon={<Download className="text-indigo-500" />}
            color="indigo"
          />
        </div>

        {/* SEARCH TOTAL CARD - Show when searching on Funds or Loans tab */}
        {(activeTab === 4 || activeTab === 5) &&
          searchTerm &&
          searchTotal > 0 && (
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-indigo-100 text-sm font-medium mb-1">
                    Total for "{searchTerm}" (All{" "}
                    {totalRecordsFound.toLocaleString()} Records)
                  </p>
                  <p className="text-4xl font-black mb-2">
                    ₵ {searchTotal.toLocaleString()}
                  </p>
                  <p className="text-indigo-100 text-sm">
                    Showing page {currentPage} of{" "}
                    {Math.ceil((paginate?.totalItems || 0) / perPage)}
                    {" • "}
                    {filteredData.length} records on this page
                  </p>
                </div>
                <div className="bg-white/20 backdrop-blur-sm p-4 rounded-xl">
                  <Search size={32} />
                </div>
              </div>
            </div>
          )}

        {/* DATA TABLE SECTION */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden transition-colors">
          <div className="p-6 border-b border-slate-100 dark:border-gray-700 flex flex-col md:flex-row justify-between items-start gap-4">
            <div className="flex-1 w-full">
              <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-4">
                {activeTab === 2
                  ? "Incremental Requests"
                  : activeTab === 3
                  ? "Decremental Requests"
                  : activeTab === 4
                  ? "Fund Deduction History"
                  : "Loans Deduction"}
              </h2>

              {/* Search Bar */}
              <div className="relative max-w-md">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500"
                  size={20}
                />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder={
                    activeTab === 4 || activeTab === 5
                      ? "Search by staff ID"
                      : "Search by staff ID"
                  }
                  className="w-full pl-10 pr-10 py-2.5 bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-600 rounded-lg text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                />
                {searchTerm && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>

              <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                {activeTab === 5 || activeTab === 4 ? (
                  <>
                    Showing {filteredData.length} of{" "}
                    {paginate?.totalItems?.toLocaleString() || 0} total records
                    {searchTerm && (
                      <span className="text-indigo-600 font-medium ml-2">
                        • Filtered results
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    {filteredData.length} of {getCurrentData().length} records
                    {searchTerm && filteredData.length > 0 && (
                      <span className="text-indigo-600 font-medium ml-2">
                        • {filteredData.length} result
                        {filteredData.length !== 1 ? "s" : ""}
                      </span>
                    )}
                  </>
                )}
              </p>
            </div>

            <div className="flex flex-col md:flex-row gap-3 items-center">
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="flex items-center gap-2 px-4 py-2.5 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors"
                >
                  <X size={18} />
                  Clear Search
                </button>
              )}
              {activeTab !== 4 && activeTab !== 5 && (
                <button
                  onClick={() => setIsExportModalOpen(true)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm font-medium"
                >
                  <Download size={18} />
                  Export Report
                </button>
              )}
            </div>
          </div>

          <DataTable
            columns={
              activeTab === 4
                ? deductionColumns
                : activeTab === 5
                ? loanDeductionColumns
                : incrementDecrementColumns
            }
            data={filteredData}
            pagination
            paginationServer={activeTab === 5 || activeTab === 4}
            paginationTotalRows={
              activeTab === 5 || activeTab === 4
                ? paginate?.totalItems
                : filteredData.length
            }
            onChangePage={
              activeTab === 5 || activeTab === 4 ? handlePageChange : undefined
            }
            onChangeRowsPerPage={
              activeTab === 5 || activeTab === 4
                ? handlePerRowsChange
                : undefined
            }
            paginationPerPage={perPage}
            paginationRowsPerPageOptions={[20, 50, 100, 150, 250, 500]}
            highlightOnHover
            pointerOnHover
            progressPending={isLoading}
            customStyles={customStyles}
            responsive
            noDataComponent={
              <div className="py-12 text-center bg-white dark:bg-gray-800 w-full">
                <div className="mx-auto w-16 h-16 bg-slate-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
                  <Search className="text-slate-400 dark:text-slate-500" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-2">
                  {searchTerm ? "No results found" : "No data available"}
                </h3>
                <p className="text-slate-500 dark:text-slate-400">
                  {searchTerm
                    ? `No records found for "${searchTerm}"`
                    : "There are no records to display"}
                </p>
                {searchTerm && (
                  <button
                    onClick={clearSearch}
                    className="mt-4 px-4 py-2 text-indigo-600 hover:text-indigo-800 font-medium"
                  >
                    Clear search
                  </button>
                )}
              </div>
            }
          />
        </div>
      </div>

      {/* EXPORT MODAL */}
      {isExportModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-6 bg-indigo-600 text-white">
              <h2 className="text-xl font-bold">Generate Export</h2>
              <p className="text-indigo-100 text-sm mt-1">
                Select the timeframe for the excel report
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full mt-1 bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-600 text-slate-800 dark:text-slate-100 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full mt-1 bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-600 text-slate-800 dark:text-slate-100 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                />
              </div>
            </div>
            <div className="p-6 bg-slate-50 dark:bg-gray-900/50 flex gap-3">
              <button
                onClick={() => setIsExportModalOpen(false)}
                className="flex-1 px-4 py-2.5 text-slate-600 dark:text-slate-300 font-semibold hover:bg-slate-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                className="flex-[2] px-4 py-2.5 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 shadow-md shadow-indigo-200"
              >
                Download Excel
              </button>
            </div>
          </div>
        </div>
      )}
    </RenderUnionPage>
  );
};

const StatCard = ({ title, value, subtitle, icon, color }) => {
  const colors = {
    emerald:
      "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 border-emerald-100 dark:border-emerald-800",
    rose: "bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 border-rose-100 dark:border-rose-800",
    amber:
      "bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 border-amber-100 dark:border-amber-800",
    indigo:
      "bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-indigo-100 dark:border-indigo-800",
    cyan: "bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 border-cyan-100 dark:border-cyan-800",
  };

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm flex items-center justify-between transition-colors">
      <div>
        <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
          {title}
        </p>
        <p className="text-3xl font-black text-slate-800 dark:text-slate-100">
          {typeof value === "string" ? value : value.toLocaleString()}
        </p>
        {subtitle && (
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
            {subtitle}
          </p>
        )}
      </div>
      <div className={`p-4 rounded-xl border ${colors[color]}`}>
        {React.cloneElement(icon, { size: 28 })}
      </div>
    </div>
  );
};

export default Dashboard;
